<script>
    import { createEventDispatcher } from 'svelte';
    import { surveyStore } from '../../stores/surveyStore';
    import { onMount } from 'svelte';
    import { v4 as uuidv4 } from 'uuid'; 
    
    const dispatch = createEventDispatcher();
    
    let email = '';
    let isSubmitting = false;
    let hasError = false;
    let errorMessage = '';
    let showSkipButton = false;
    let skipButtonTimer;
    
    onMount(() => {
      // Show skip button after 4 seconds
      skipButtonTimer = setTimeout(() => {
        showSkipButton = true;
      }, 4000);
      
      return () => {
        // Clear the timer when component is destroyed
        clearTimeout(skipButtonTimer);
      };
    });
    // Email validation function
    function isValidEmail(email) {
      const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return regex.test(email);
    }
    
    // Function to handle skip
    function handleSkip() {
      // Generate a GUID for anonymous tracking
      const anonymousId = uuidv4();
      
      // Update store with anonymous ID instead of email
      surveyStore.update(store => ({
        ...store,
        isAnonymous: true,
        anonymousId: anonymousId,
        // We'll still include a "email" field for API consistency
        email: `anonymous-${anonymousId}@survey.local`
      }));

      // Move to next question
      dispatch('next');
    }

    // Handle form submission
    async function handleSubmit() {
      // Reset error state
      hasError = false;
      errorMessage = '';
      
      // Validate email
      if (!email) {
        hasError = true;
        errorMessage = 'Please enter your email address.';
        return;
      }
      
      if (!isValidEmail(email)) {
        hasError = true;
        errorMessage = 'Please enter a valid email address.';
        return;
      }

      // Set loading state
      isSubmitting = true;
      
      try {
        // Update store with email
        surveyStore.update(store => ({
          ...store,
          email
        }));
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Move to next question
        dispatch('next');
      } catch (error) {
        console.error('Error submitting email:', error);
        hasError = true;
        errorMessage = 'An error occurred. Please try again.';
      } finally {
        isSubmitting = false;
      }
    }
  </script>
  
  <div class="email-capture">
    <h2>Where should we send your results?</h2>
    <p class="description">We'll email your personalized membership recommendation.</p>
    
    <form on:submit|preventDefault={handleSubmit}>
      <div class="form-group {hasError ? 'has-error' : ''}">
        <input 
          type="email" 
          id="email" 
          placeholder="Your email address"
          bind:value={email}
          class={hasError ? 'error' : ''}
          disabled={isSubmitting}
          autocomplete="email"
        />
        
        {#if hasError}
          <div class="error-message">{errorMessage}</div>
        {/if}
      </div>
      
      <button 
        type="submit" 
        class="submit-button" 
        disabled={isSubmitting}
      >
        {#if isSubmitting}
          <div class="spinner"></div>
        {:else}
          Continue
        {/if}
      </button>

      {#if showSkipButton}
      <button 
        type="button" 
        class="skip-button" 
        on:click={handleSkip}
      >
        Skip for now
      </button>
      {/if}
      
      <p class="privacy-note">
        By continuing, you agree to our 
        <a href="https://www.nba.com/cavaliers/legal/privacy-policy" target="_blank">Privacy Policy</a>.
        We'll send you communications about Cavs Memberships. You can unsubscribe anytime.
      </p>
    </form>
  </div>
  
  <style>
    .skip-button {
      width: 100%;
      background-color: transparent;
      color: #888;
      border: none;
      padding: 0.5rem;
      font-size: 0.9rem;
      cursor: pointer;
      transition: color 0.2s ease;
      margin-top: 0.5rem;
      box-shadow: none;
    }
    
    .skip-button:hover {
      color: #6A0032; /* Cavs wine color */
      background-color: transparent;
    }
    .email-capture {
      width: 100%;
      max-width: 500px;
      margin: 0 auto;
    }
    
    h2 {
      font-size: 1.8rem;
      color: #333;
      margin-bottom: 1rem;
      font-weight: 600;
      text-align: center;
    }
    
    .description {
      font-size: 1.1rem;
      color: #666;
      margin-bottom: 2rem;
      text-align: center;
    }
    
    .form-group {
      margin-bottom: 1.5rem;
    }
    
    input {
      width: 100%;
      padding: 1rem;
      border: 2px solid #e0e0e0;
      border-radius: 8px;
      font-size: 1.1rem;
      transition: all 0.2s ease;
    }
    
    input:focus {
      outline: none;
      border-color: #BB945C; /* Cavs gold color */
      box-shadow: 0 0 0 3px rgba(253, 187, 48, 0.2);
    }
    
    input.error {
      border-color: #e53935;
    }
    
    .error-message {
      color: #e53935;
      font-size: 0.9rem;
      margin-top: 0.5rem;
    }
    
    .submit-button {
      width: 100%;
      background-color: #6A0032; /* Cavs wine color */
      color: white;
      border: none;
      padding: 1rem;
      font-size: 1.1rem;
      border-radius: 8px;
      cursor: pointer;
      transition: background-color 0.2s ease;
      font-weight: 600;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    
    .submit-button:hover:not(:disabled) {
      background-color: #6d0030;
    }
    
    .submit-button:disabled {
      background-color: #cccccc;
      cursor: not-allowed;
    }
    
    .spinner {
      width: 20px;
      height: 20px;
      border: 3px solid rgba(255, 255, 255, 0.3);
      border-radius: 50%;
      border-top-color: white;
      animation: spin 1s ease-in-out infinite;
    }
    
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    
    .privacy-note {
      font-size: 0.8rem;
      color: #888;
      margin-top: 1.5rem;
      text-align: center;
    }
    
    .privacy-note a {
      color: #6A0032;
      text-decoration: none;
    }
    
    .privacy-note a:hover {
      text-decoration: underline;
    }
  </style>